import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ClientQuotaListRow } from '../../../../core/models/card-quota/client-quota.model';
import { MapperClientQuotaService } from '../../../../core/mappers/card-quota/mapper-client-quota.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalClientQuotaDetailComponent } from '../../modals/card-quota/modal-client-quota-detail/modal-client-quota-detail.component';

type SecondaryFilterMode = '' | 'level' | 'status';

@Component({
  selector: 'app-client-quotas',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalClientQuotaDetailComponent],
  templateUrl: './client-quotas.component.html',
  styleUrl: './client-quotas.component.css',
})
export class ClientQuotasComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperClientQuotaService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  items = signal<ClientQuotaListRow[]>([]);
  isLoading = signal(false);
  isDetailOpen = signal(false);
  selectedQuotaId = signal(0);

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  secondaryFilterMode = signal<SecondaryFilterMode>('');
  filterQuotaLevel = signal('');
  filterQuotaStatus = signal('');

  searchField = signal<'name' | 'site'>('name');
  searchText = signal('');

  readonly searchFieldOptions = [
    { value: 'name' as const, label: 'ชื่อโควต้า' },
    { value: 'site' as const, label: 'ชื่อลาน' },
  ];

  readonly levelOptions = [
    { value: '', label: 'ทุก Level' },
    { value: 'master', label: 'ฝ่ายอาคาร (โควต้าหลัก)' },
    { value: 'sub', label: 'ร้านค้า (โควต้าย่อย)' },
  ];

  readonly statusOptions = [
    { value: '', label: 'ทุกสถานะ' },
    { value: 'active', label: 'ใช้งาน' },
    { value: 'suspended', label: 'ระงับ' },
    { value: 'pending', label: 'รออนุมัติ' },
  ];

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadScopeAndTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  levelLabel = (v: string) => this.mapper.quotaLevelLabel(v);
  statusLabel = (v: string) => this.mapper.quotaStatusLabel(v);

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadScopeAndTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadScopeAndTable(true);
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onSiteChange(siteId: number): void {
    const site = this.siteState.sites().find((s) => s.id === Number(siteId));
    if (!site) return;
    this.siteState.setSelectedSite(site);
  }

  onSecondaryFilterModeChange(mode: SecondaryFilterMode): void {
    this.secondaryFilterMode.set(mode);
    if (mode !== 'level') this.filterQuotaLevel.set('');
    if (mode !== 'status') this.filterQuotaStatus.set('');
    this.onSearch();
  }

  onLevelChange(value: string): void {
    this.secondaryFilterMode.set(value ? 'level' : '');
    this.filterQuotaLevel.set(value);
    this.filterQuotaStatus.set('');
    this.onSearch();
  }

  onStatusChange(value: string): void {
    this.secondaryFilterMode.set(value ? 'status' : '');
    this.filterQuotaStatus.set(value);
    this.filterQuotaLevel.set('');
    this.onSearch();
  }

  clearSearchText(): void {
    this.searchText.set('');
    this.onSearch();
  }

  loadScopeAndTable(forceScope = false): void {
    const siteId = this.selectedSiteId;
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.clientScope.load(siteId, userId, forceScope).subscribe({
      next: () => this.loadTable(),
      error: () => this.loadTable(),
    });
  }

  openDetail(item: ClientQuotaListRow): void {
    this.selectedQuotaId.set(item.id);
    this.isDetailOpen.set(true);
  }

  closeDetail(): void {
    this.isDetailOpen.set(false);
    this.selectedQuotaId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_index: number, item: ClientQuotaListRow): number {
    return item.id;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      this.isLoading.set(false);
      return;
    }

    const trimmedSearch = this.searchText().trim();
    const searchField = trimmedSearch ? this.searchField() : null;
    const searchText = trimmedSearch || null;

    this.isLoading.set(true);
    this.api
      .getClientQuotasForTable(
        siteId,
        this.currentPage(),
        this.pageSize(),
        null,
        this.secondaryFilterMode() === 'level' ? this.filterQuotaLevel() || null : null,
        this.secondaryFilterMode() === 'status' ? this.filterQuotaStatus() || null : null,
        searchField,
        searchText
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดรายการโควต้าล้มเหลว');
        },
      });
  }
}
